import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { C, FONT, R, SP } from '../theme/tokens';
import { CALC_REGISTRY, CalcCategory, findCalc } from '../utils/calcRegistry';
import { FavoriteEntry, loadFavorites } from '../utils/favorites';
import { HistoryEntry, loadHistory } from '../utils/history';
import { loadUsedCalcs } from '../utils/usedCalcs';

type Nav = {
  navigate: (screen: string, params?: any) => void;
  addListener: (event: string, cb: () => void) => () => void;
};

type CategoryTile = {
  code: string;
  name: string;
  category: CalcCategory;
  overviewScreen: string;
};

const CATEGORY_TILES: CategoryTile[] = [
  { code: 'TAB', name: 'Testing, Adjusting & Balancing', category: 'TAB', overviewScreen: 'TABOverview' },
  { code: 'CLR', name: 'Cleanroom Testing', category: 'Cleanroom', overviewScreen: 'CleanroomCalculations' },
  { code: 'FUM', name: 'Fume Hood', category: 'Fumehood', overviewScreen: 'FumehoodCalculations' },
  { code: 'BET', name: 'Building Envelope Testing', category: 'BET', overviewScreen: 'BETCalculations' },
];

// Total calcs per category, derived once from the registry.
const TOTALS: Record<CalcCategory, number> = CALC_REGISTRY.reduce(
  (acc, c) => {
    acc[c.category] = (acc[c.category] ?? 0) + 1;
    return acc;
  },
  { TAB: 0, Cleanroom: 0, Fumehood: 0, BET: 0 } as Record<CalcCategory, number>,
);

const TOTAL_CALCS = Object.values(TOTALS).reduce((a, b) => a + b, 0);

function relTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

const HomeScreen = ({ navigation }: { navigation: Nav }) => {
  const [favorites, setFavorites] = useState<FavoriteEntry[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [openedByCat, setOpenedByCat] = useState<Record<CalcCategory, number>>({
    TAB: 0, Cleanroom: 0, Fumehood: 0, BET: 0,
  });

  const refresh = useCallback(async () => {
    const [favs, hist, used] = await Promise.all([
      loadFavorites(),
      loadHistory(),
      loadUsedCalcs(),
    ]);
    setFavorites(favs);
    setHistory(hist);

    // Tally opened calcs per category using the registry for authoritative mapping.
    const tally: Record<CalcCategory, number> = { TAB: 0, Cleanroom: 0, Fumehood: 0, BET: 0 };
    Object.keys(used.routes).forEach((route) => {
      const calc = findCalc(route);
      if (calc) tally[calc.category] = (tally[calc.category] ?? 0) + 1;
    });
    setOpenedByCat(tally);
  }, []);

  useEffect(() => {
    const unsub = navigation.addListener('focus', refresh);
    return unsub;
  }, [navigation, refresh]);

  const totalOpened = useMemo(
    () => Object.values(openedByCat).reduce((a, b) => a + b, 0),
    [openedByCat],
  );

  const recents = history.slice(0, 4);

  const openFav = (fav: FavoriteEntry) => {
    navigation.navigate(fav.category, { screen: fav.route });
  };

  const openHistoryEntry = (entry: HistoryEntry) => {
    navigation.navigate(entry.category, { screen: entry.route });
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.inner}>
        {/* ── Brand mark ── */}
        <View style={styles.brandRow}>
          <Text style={styles.brandMark}>
            Aero<Text style={styles.brandDot}>Viz</Text> Core
          </Text>
          <Text style={styles.brandSub}>FIELD CALCULATOR SUITE</Text>
        </View>

        {/* ── Search bar (⌘K palette TBD) ── */}
        <Pressable
          style={({ pressed }) => [styles.searchBar, pressed && styles.pressed]}
          onPress={() => navigation.navigate('CalcSearch')}
          accessibilityRole="search"
          hitSlop={8}
        >
          <Text style={styles.searchGlyph}>/</Text>
          <Text style={styles.searchPlaceholder}>
            Search {TOTAL_CALCS} calculators
          </Text>
          <Text style={styles.searchKbd}>⌘K</Text>
        </Pressable>

        {/* ── Category tiles with coverage bars ── */}
        <Text style={styles.sectionLabel}>Categories</Text>
        <View style={styles.catGrid}>
          {CATEGORY_TILES.map((tile) => {
            const total = TOTALS[tile.category];
            const opened = openedByCat[tile.category] ?? 0;
            const pct = total > 0 ? Math.min(100, (opened / total) * 100) : 0;
            return (
              <Pressable
                key={tile.code}
                style={({ pressed }) => [styles.catTile, pressed && styles.catTilePressed]}
                onPress={() => navigation.navigate(tile.category, { screen: tile.overviewScreen })}
                accessibilityRole="button"
              >
                <View style={styles.catTileHead}>
                  <Text style={styles.catCode}>{tile.code}</Text>
                  <Text style={styles.catCount}>
                    {opened}/{total}
                  </Text>
                </View>
                <Text style={styles.catName} numberOfLines={2}>
                  {tile.name}
                </Text>
                <View style={styles.catBar}>
                  <View style={[styles.catBarFill, { width: `${pct}%` }]} />
                </View>
              </Pressable>
            );
          })}
        </View>

        {/* ── Favorites strip ── */}
        {favorites.length > 0 ? (
          <>
            <Text style={styles.sectionLabel}>Favorites</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.favStrip}
              contentContainerStyle={styles.favStripInner}
            >
              {favorites.map((fav) => (
                <Pressable
                  key={fav.route}
                  style={({ pressed }) => [styles.favChip, pressed && styles.pressed]}
                  onPress={() => openFav(fav)}
                  accessibilityRole="button"
                >
                  <Text style={styles.favLabel} numberOfLines={2}>
                    {fav.title}
                  </Text>
                  <Text style={styles.favSub}>{fav.category.toUpperCase()}</Text>
                </Pressable>
              ))}
            </ScrollView>
          </>
        ) : null}

        {/* ── Coverage + Recents row ── */}
        <View style={styles.lowerRow}>
          {/* Coverage */}
          <Pressable
            style={({ pressed }) => [styles.lowerCard, pressed && styles.pressed]}
            onPress={() => navigation.navigate('Progress')}
            accessibilityRole="button"
          >
            <View style={styles.coverageHead}>
              <Text style={styles.sectionLabelInline}>Coverage</Text>
              <Text style={styles.coverageFrac}>
                <Text style={styles.coverageFracAccent}>{totalOpened}</Text>
                <Text>/{TOTAL_CALCS}</Text>
              </Text>
            </View>
            <Text style={styles.coverageSub}>calculators opened at least once</Text>
            <View style={styles.coverageBars}>
              {CATEGORY_TILES.map((tile) => {
                const total = TOTALS[tile.category];
                const opened = openedByCat[tile.category] ?? 0;
                const pct = total > 0 ? Math.min(100, (opened / total) * 100) : 0;
                return (
                  <View key={tile.code} style={styles.coverageBarRow}>
                    <Text style={styles.coverageBarLabel}>{tile.code}</Text>
                    <View style={styles.coverageBarTrack}>
                      <View style={[styles.coverageBarFill, { width: `${pct}%` }]} />
                    </View>
                    <Text style={styles.coverageBarFrac}>{opened}/{total}</Text>
                  </View>
                );
              })}
            </View>
          </Pressable>

          {/* Recents */}
          <View style={styles.lowerCard}>
            <View style={styles.recentsHead}>
              <Text style={styles.sectionLabelInline}>Recent</Text>
              <Pressable
                onPress={() => navigation.navigate('RecentCalculations')}
                accessibilityRole="button"
                hitSlop={8}
              >
                <Text style={styles.viewAll}>View all →</Text>
              </Pressable>
            </View>
            {recents.length === 0 ? (
              <Text style={styles.recentsEmpty}>
                Completed calculations appear here automatically.
              </Text>
            ) : (
              <View style={styles.recentsList}>
                {recents.map((r, i) => {
                  // Pick the hero result line for display.
                  const hero = r.result[0];
                  return (
                    <Pressable
                      key={`${r.route}-${r.ts}-${i}`}
                      style={({ pressed }) => [
                        styles.recentRow,
                        i === recents.length - 1 && styles.recentRowLast,
                        pressed && styles.pressed,
                      ]}
                      onPress={() => openHistoryEntry(r)}
                    >
                      <Text style={styles.recentName} numberOfLines={1}>
                        {r.title}
                      </Text>
                      <Text style={styles.recentResult}>
                        <Text style={styles.recentValue}>{hero?.value ?? '—'}</Text>
                        {hero?.unit ? <Text style={styles.recentUnit}> {hero.unit}</Text> : null}
                      </Text>
                      <Text style={styles.recentWhen}>{relTime(r.ts)}</Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </View>
        </View>

        {/* ── Footer utility row ── */}
        <View style={styles.footer}>
          <Pressable
            style={({ pressed }) => [styles.footerBtn, pressed && styles.pressed]}
            onPress={() => navigation.navigate('History')}
            accessibilityRole="button"
          >
            <Ionicons name="document-text-outline" size={14} color={C.ink2} />
            <Text style={styles.footerBtnText}>History</Text>
          </Pressable>
          <Pressable
            style={({ pressed }) => [styles.footerBtn, pressed && styles.pressed]}
            onPress={() => navigation.navigate('Settings')}
            accessibilityRole="button"
          >
            <Ionicons name="settings-outline" size={14} color={C.ink2} />
            <Text style={styles.footerBtnText}>Settings</Text>
          </Pressable>
          <Text style={styles.footerMeta}>v0.0.1 · offline</Text>
        </View>

        {/* ── Disclaimer ── */}
        <View style={styles.disclaimerCard}>
          <View style={styles.disclaimerHeader}>
            <Ionicons name="warning-outline" size={16} color={C.accent} style={{ marginRight: SP.sm }} />
            <Text style={styles.disclaimerTitle}>Educational use only</Text>
          </View>
          <Text style={styles.disclaimerBody}>
            AeroViz Core is provided for educational purposes only. All results must be independently verified
            by the user against field measurements before being used for any engineering, compliance, or
            safety decision.
          </Text>
          <Text style={styles.disclaimerBody}>
            This app is developed independently and is not affiliated with, endorsed by, or associated with
            any certification body, trade organization, or standards-development body.
          </Text>
          <Text style={styles.disclaimerBody}>
            All equations are derived from publicly available mathematics, physics, and engineering
            principles. No proprietary formulas or organizational content is included.
          </Text>
          <Text style={styles.disclaimerBody}>
            Use at your own risk. The developer disclaims all liability for any decisions, damages, or losses
            arising from use of this application.
          </Text>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg },
  contentContainer: { padding: SP.md, paddingBottom: SP.xl + SP.md },
  inner: { width: '100%', maxWidth: 980, alignSelf: 'center' },

  // Brand
  brandRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: SP.sm + 4,
    marginBottom: SP.md,
  },
  brandMark: {
    fontFamily: FONT.monoBold,
    fontSize: 22,
    color: C.ink,
    letterSpacing: -0.3,
  },
  brandDot: { color: C.accent },
  brandSub: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    letterSpacing: 1.2,
  },

  // Search bar
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm + 4,
    backgroundColor: C.panel,
    borderWidth: 1,
    borderColor: C.edge,
    borderRadius: R.field,
    paddingHorizontal: SP.md,
    paddingVertical: 14,
    marginBottom: SP.lg,
  },
  searchGlyph: {
    fontFamily: FONT.mono,
    fontSize: 14,
    color: C.dim,
    width: 18,
    textAlign: 'center',
  },
  searchPlaceholder: {
    flex: 1,
    fontFamily: FONT.sans,
    fontSize: 14,
    color: C.ink2,
  },
  searchKbd: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    borderWidth: 1,
    borderColor: C.edge,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },

  // Section labels
  sectionLabel: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    marginTop: SP.sm,
    marginBottom: SP.sm + 4,
  },
  sectionLabelInline: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },

  // Category tiles
  catGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SP.sm + 2,
  },
  catTile: {
    flexGrow: 1,
    flexBasis: '47%',
    minWidth: 140,
    backgroundColor: C.panel,
    borderWidth: 1,
    borderColor: C.edge,
    borderRadius: R.card,
    padding: SP.md,
    minHeight: 110,
    justifyContent: 'space-between',
  },
  catTilePressed: { borderColor: '#31373D', backgroundColor: C.panel2 },
  catTileHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: SP.sm,
  },
  catCode: {
    fontFamily: FONT.monoBold,
    fontSize: 11,
    color: C.accent,
    letterSpacing: 1.1,
  },
  catCount: {
    fontFamily: FONT.mono,
    fontSize: 11,
    color: C.dim,
  },
  catName: {
    fontFamily: FONT.sansMed,
    fontSize: 14,
    color: C.ink,
    lineHeight: 18,
    marginBottom: SP.sm + 4,
  },
  catBar: {
    height: 2,
    backgroundColor: C.edge,
    borderRadius: 1,
    overflow: 'hidden',
  },
  catBarFill: {
    height: '100%',
    backgroundColor: C.accent,
  },

  // Favorites strip
  favStrip: { marginBottom: SP.sm, marginHorizontal: -SP.md },
  favStripInner: { paddingHorizontal: SP.md, gap: SP.sm, paddingBottom: 2 },
  favChip: {
    backgroundColor: C.panel,
    borderWidth: 1,
    borderColor: C.edge,
    borderRadius: R.field,
    padding: SP.sm + 4,
    minWidth: 160,
    maxWidth: 220,
  },
  favLabel: {
    fontFamily: FONT.sansMed,
    fontSize: 13,
    color: C.ink,
    lineHeight: 17,
  },
  favSub: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    letterSpacing: 0.6,
    marginTop: 4,
  },

  // Coverage + Recents row
  lowerRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SP.sm + 2,
    marginTop: SP.md,
  },
  lowerCard: {
    flexGrow: 1,
    flexBasis: 280,
    backgroundColor: C.panel,
    borderWidth: 1,
    borderColor: C.edge,
    borderRadius: R.card,
    padding: SP.md,
  },
  coverageHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 2,
  },
  coverageFrac: {
    fontFamily: FONT.sansBold,
    fontSize: 22,
    color: C.ink,
    letterSpacing: -0.4,
  },
  coverageFracAccent: { color: C.accent },
  coverageSub: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    marginBottom: SP.md,
  },
  coverageBars: { gap: SP.sm + 2 },
  coverageBarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm + 4,
  },
  coverageBarLabel: {
    fontFamily: FONT.mono,
    fontSize: 11,
    color: C.ink2,
    width: 36,
  },
  coverageBarTrack: {
    flex: 1,
    height: 4,
    backgroundColor: C.edge,
    borderRadius: 2,
    overflow: 'hidden',
  },
  coverageBarFill: {
    height: '100%',
    backgroundColor: C.ink2,
    borderRadius: 2,
  },
  coverageBarFrac: {
    fontFamily: FONT.mono,
    fontSize: 11,
    color: C.dim,
    textAlign: 'right',
    width: 48,
  },

  // Recents
  recentsHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SP.sm,
  },
  viewAll: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.ink2,
    letterSpacing: 0.6,
  },
  recentsList: { marginTop: 2 },
  recentsEmpty: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    marginTop: SP.sm,
    lineHeight: 18,
  },
  recentRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: SP.sm + 4,
    paddingVertical: SP.sm + 4,
    borderBottomWidth: 1,
    borderBottomColor: C.edge,
  },
  recentRowLast: { borderBottomWidth: 0 },
  recentName: {
    flex: 1,
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink,
  },
  recentResult: { flexDirection: 'row', alignItems: 'baseline' },
  recentValue: { fontFamily: FONT.monoBold, fontSize: 13, color: C.ink },
  recentUnit: { fontFamily: FONT.mono, fontSize: 10, color: C.dim },
  recentWhen: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    minWidth: 40,
    textAlign: 'right',
  },

  // Footer
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm + 2,
    marginTop: SP.lg,
    paddingTop: SP.md + 4,
    borderTopWidth: 1,
    borderTopColor: C.edge,
  },
  footerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: SP.sm + 6,
    paddingVertical: SP.sm,
    borderWidth: 1,
    borderColor: C.edge,
    borderRadius: 8,
  },
  footerBtnText: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.ink2,
  },
  footerMeta: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    marginLeft: 'auto',
    letterSpacing: 0.4,
  },

  legal: {
    fontFamily: FONT.sans,
    fontSize: 11,
    color: C.dim,
    textAlign: 'center',
    marginTop: SP.md,
    lineHeight: 17,
  },

  disclaimerCard: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginTop: SP.md,
  },
  disclaimerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: SP.xs,
  },
  disclaimerTitle: {
    fontFamily: FONT.sansBold,
    fontSize: 13,
    color: C.ink,
    letterSpacing: 0.2,
  },
  disclaimerBody: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.ink2,
    lineHeight: 18,
    marginTop: SP.sm,
  },

  pressed: { opacity: 0.7 },
});

export default HomeScreen;
